/**
 * localization-form.js  (v6 — Bucks currency override on page load)
 *
 * Key fix vs v5:
 *  - On every page load, reads bucks_currency cookie and forces Bucks to use
 *    that currency BEFORE Bucks auto-detects from IP.
 *  - Uses multiple override strategies: Object.defineProperty intercept,
 *    early cookie/localStorage write, and post-load re-assertion.
 *  - This means: if user picks United States from India, USD stays on reload.
 *    If user picks India from US VPN, INR stays on reload.
 */

/* ─────────────────────────────────────────────
   STEP 1 — Run immediately (before anything else loads)
   Read the user-chosen currency from cookie and lock it in
   so Bucks cannot override it with IP-based detection.
   ───────────────────────────────────────────── */
(function lockBucksCurrencyEarly() {
  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|;)\\s*' + name + '=([^;]+)'));'(?:^|;)\\s*'
    return m ? decodeURIComponent(m[1]) : null;
  }

  /* Step 1: Read ?currency= from URL (set by our redirect after market switch) */
  var urlCurrency = null;
  try {
    urlCurrency = new URLSearchParams(window.location.search).get('currency');
    if (urlCurrency && /^[A-Z]{3}$/.test(urlCurrency)) {
      var exp = new Date(); exp.setFullYear(exp.getFullYear() + 1);
      ['bucksCurrency','bucks_currency','currency','cart_currency'].forEach(function(k) {
        document.cookie = k + '=' + urlCurrency + '; path=/; expires=' + exp.toUTCString();
      });
      try {
        ['bucksCurrency','bucks_currency','currency','nova_currency','mlveda_currency','user_chosen_currency'].forEach(function(k) {
          localStorage.setItem(k, urlCurrency);
        });
      } catch(e) {}
    }
  } catch(e) {}

  /* Step 2: Read chosen currency from all possible sources */
  var chosen = urlCurrency ||
               getCookie('bucksCurrency') ||
               getCookie('bucks_currency') ||
               getCookie('currency');
  try {
    chosen = chosen ||
             localStorage.getItem('bucksCurrency') ||
             localStorage.getItem('bucks_currency') ||
             localStorage.getItem('user_chosen_currency');
  } catch(e) {}

  if (!chosen || !/^[A-Z]{3}$/.test(chosen)) return;

  /* Step 3: Write ALL keys so Bucks finds our value */
  var exp2 = new Date(); exp2.setFullYear(exp2.getFullYear() + 1);
  ['bucksCurrency','bucks_currency','currency','cart_currency'].forEach(function(k) {
    document.cookie = k + '=' + chosen + '; path=/; expires=' + exp2.toUTCString();
  });
  try {
    ['bucksCurrency','bucks_currency','currency','nova_currency','mlveda_currency','user_chosen_currency'].forEach(function(k) {
      localStorage.setItem(k, chosen);
    });
  } catch(e) {}

  /* Step 4: Force Shopify currency object */
  try {
    window.Shopify = window.Shopify || {};
    window.Shopify.currency = window.Shopify.currency || {};
    window.Shopify.currency.active = chosen;
  } catch(e) {}

  /* Step 5: Click the correct Bucks selector option */
  function forceBucksSelector() {
    var sel = document.getElementById('bucksSelector');
    if (sel && sel.value !== chosen) {
      sel.value = chosen;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    document.querySelectorAll('.buckscc-select-options li').forEach(function(li) {
      var isGeo = li.classList.contains('geoCurrency');
      var matches = li.getAttribute('rel') === chosen || li.id === chosen;
      if (matches && !isGeo) {
        li.click();
      }
    });
    /* Also force all price elements directly */
    document.querySelectorAll('.buckscc-money, .money').forEach(function(el) {
      el.setAttribute('data-forced-currency', chosen);
    });
  }

  /* Step 6: Intercept Bucks global objects */
  ['Bucks','BucksApp','buckscc'].forEach(function(name) {
    try {
      var real = window[name];
      Object.defineProperty(window, name, {
        configurable: true,
        get: function() { return real; },
        set: function(val) {
          real = val;
          if (val && typeof val.setCurrency === 'function') {
            setTimeout(function() { try { val.setCurrency(chosen); } catch(e) {} }, 0);
            setTimeout(function() { try { val.setCurrency(chosen); } catch(e) {} }, 500);
          }
        }
      });
    } catch(e) {}
  });

  /* Step 7: Hammer at multiple intervals to beat lazy Bucks init */
  function forceAll() {
    forceBucksSelector();
    ['Bucks','BucksApp','buckscc'].forEach(function(name) {
      try { if (window[name] && window[name].setCurrency) window[name].setCurrency(chosen); } catch(e) {}
    });
  }
  [0, 50, 100, 200, 300, 500, 800, 1000, 1500, 2000, 3000, 5000].forEach(function(ms) {
    setTimeout(forceAll, ms);
  });
  window.addEventListener('load', function() {
    [0, 200, 500, 1000, 2000, 4000].forEach(function(ms) { setTimeout(forceAll, ms); });
  });
})();


/* ─────────────────────────────────────────────
   Continent lookup table (ISO-2 → region name)
   ───────────────────────────────────────────── */
const CONTINENT_MAP = {
  DZ:'Africa',EG:'Africa',ET:'Africa',GH:'Africa',KE:'Africa',MA:'Africa',
  MZ:'Africa',NG:'Africa',SN:'Africa',ZA:'Africa',TZ:'Africa',TN:'Africa',
  UG:'Africa',CI:'Africa',CM:'Africa',AO:'Africa',BJ:'Africa',BW:'Africa',
  BF:'Africa',BI:'Africa',CV:'Africa',CF:'Africa',TD:'Africa',KM:'Africa',
  CD:'Africa',CG:'Africa',DJ:'Africa',GQ:'Africa',ER:'Africa',SZ:'Africa',
  GA:'Africa',GM:'Africa',GN:'Africa',GW:'Africa',LS:'Africa',LR:'Africa',
  LY:'Africa',MG:'Africa',MW:'Africa',ML:'Africa',MR:'Africa',MU:'Africa',
  NA:'Africa',NE:'Africa',RW:'Africa',ST:'Africa',SC:'Africa',SL:'Africa',
  SO:'Africa',SS:'Africa',SD:'Africa',TG:'Africa',ZM:'Africa',ZW:'Africa',
  RE:'Africa',YT:'Africa',
  AR:'Americas',BO:'Americas',BR:'Americas',CA:'Americas',CL:'Americas',
  CO:'Americas',CR:'Americas',CU:'Americas',DO:'Americas',EC:'Americas',
  SV:'Americas',GT:'Americas',HT:'Americas',HN:'Americas',JM:'Americas',
  MX:'Americas',NI:'Americas',PA:'Americas',PY:'Americas',PE:'Americas',
  PR:'Americas',TT:'Americas',US:'Americas',UY:'Americas',VE:'Americas',
  BB:'Americas',BZ:'Americas',GY:'Americas',SR:'Americas',
  AF:'Asia',AM:'Asia',AZ:'Asia',BH:'Asia',BD:'Asia',BT:'Asia',BN:'Asia',
  KH:'Asia',CN:'Asia',CY:'Asia',GE:'Asia',HK:'Asia',IN:'Asia',ID:'Asia',
  IR:'Asia',IQ:'Asia',IL:'Asia',JP:'Asia',JO:'Asia',KZ:'Asia',KW:'Asia',
  KG:'Asia',LA:'Asia',LB:'Asia',MO:'Asia',MY:'Asia',MV:'Asia',MN:'Asia',
  MM:'Asia',NP:'Asia',KP:'Asia',OM:'Asia',PK:'Asia',PS:'Asia',PH:'Asia',
  QA:'Asia',SA:'Asia',SG:'Asia',KR:'Asia',LK:'Asia',SY:'Asia',TW:'Asia',
  TJ:'Asia',TH:'Asia',TL:'Asia',TR:'Asia',TM:'Asia',AE:'Asia',UZ:'Asia',
  VN:'Asia',YE:'Asia',
  AL:'Europe',AD:'Europe',AT:'Europe',BY:'Europe',BE:'Europe',BA:'Europe',
  BG:'Europe',HR:'Europe',CZ:'Europe',DK:'Europe',EE:'Europe',FI:'Europe',
  FR:'Europe',DE:'Europe',GR:'Europe',HU:'Europe',IS:'Europe',IE:'Europe',
  IT:'Europe',XK:'Europe',LV:'Europe',LI:'Europe',LT:'Europe',LU:'Europe',
  MT:'Europe',MD:'Europe',MC:'Europe',ME:'Europe',NL:'Europe',MK:'Europe',
  NO:'Europe',PL:'Europe',PT:'Europe',RO:'Europe',RU:'Europe',SM:'Europe',
  RS:'Europe',SK:'Europe',SI:'Europe',ES:'Europe',SE:'Europe',CH:'Europe',
  UA:'Europe',GB:'Europe',VA:'Europe',AX:'Europe',GI:'Europe',
  AU:'Oceania',FJ:'Oceania',NZ:'Oceania',PG:'Oceania',WS:'Oceania',
  SB:'Oceania',TO:'Oceania',VU:'Oceania',CK:'Oceania',PF:'Oceania',
  GU:'Oceania',NC:'Oceania',
};

const REGION_ORDER = ['Africa','Americas','Asia','Europe','Oceania','Other'];

function getRegion(iso) { return CONTINENT_MAP[iso] || 'Other'; }

/* ─────────────────────────────────────────────
   Bucks currency sync
   Called when user PICKS a country in the modal.
   Sets all possible storage keys then submits form.
   ───────────────────────────────────────────── */
function setBucksCurrency(currencyCode) {
  if (!currencyCode) return;
  const exp = new Date(); exp.setFullYear(exp.getFullYear() + 1);

  /* Write ALL cookie keys — bucksCurrency (capital C) is what Bucks actually uses */
  ['bucksCurrency','bucks_currency','currency','cart_currency'].forEach(k => {
    document.cookie = k + '=' + currencyCode + '; path=/; expires=' + exp.toUTCString();
  });
  /* Write ALL localStorage keys */
  try {
    ['bucksCurrency','bucks_currency','currency','nova_currency',
     'mlveda_currency','user_chosen_currency'].forEach(k => {
      localStorage.setItem(k, currencyCode);
    });
  } catch(e) {}

  /* Update window.Shopify.currency.active — Bucks reads this */
  try {
    window.Shopify = window.Shopify || {};
    window.Shopify.currency = window.Shopify.currency || {};
    window.Shopify.currency.active = currencyCode;
  } catch(e) {}

  /* Call Bucks API directly */
  ['Bucks','BucksApp','bucks'].forEach(name => {
    try {
      const obj = window[name];
      if (obj && typeof obj.setCurrency === 'function') obj.setCurrency(currencyCode);
    } catch(e) {}
  });

  /* Fire all possible events */
  ['bucks:currency:change','currency:change','bucks:set:currency'].forEach(evName => {
    try {
      const ev = new CustomEvent(evName, { detail: { currency: currencyCode }, bubbles: true });
      document.dispatchEvent(ev); window.dispatchEvent(ev);
    } catch(e) {}
  });
}

/* ─────────────────────────────────────────────
   STEP 2 — Post-load re-assertion
   After DOM + scripts are fully loaded, hammer the
   chosen currency one more time to beat any lazy Bucks init.
   ───────────────────────────────────────────── */
(function reassertCurrencyAfterLoad() {
  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|;)\\s*' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function doReassert() {
    const chosen = getCookie('bucksCurrency') || getCookie('bucks_currency') || getCookie('currency');
    if (!chosen) return;
    /* Re-fire setCurrency calls in case Bucks finished loading after our early intercept */
    try {
      if (window.Bucks    && typeof window.Bucks.setCurrency    === 'function') window.Bucks.setCurrency(chosen);
      if (window.BucksApp && typeof window.BucksApp.setCurrency === 'function') window.BucksApp.setCurrency(chosen);
    } catch(e) {}
    /* Re-dispatch events */
    try {
      document.dispatchEvent(new CustomEvent('bucks:currency:change', { detail: { currency: chosen } }));
      window.dispatchEvent( new CustomEvent('bucks:currency:change',  { detail: { currency: chosen } }));
    } catch(e) {}
    /* Re-write localStorage in case Bucks cleared it */
    try {
      localStorage.setItem('bucks_currency',  chosen);
      localStorage.setItem('currency',        chosen);
      localStorage.setItem('nova_currency',   chosen);
      localStorage.setItem('mlveda_currency', chosen);
    } catch(e) {}
  }

  /* Fire at DOMContentLoaded, window load, and 1s/2s/4s after load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', doReassert);
  } else {
    doReassert();
  }
  window.addEventListener('load', function() {
    doReassert();
    setTimeout(doReassert, 500);
    setTimeout(doReassert, 1000);
    setTimeout(doReassert, 2000);
    setTimeout(doReassert, 4000);
  });
})();


/* ─────────────────────────────────────────────
   Modal controller (singleton)
   ───────────────────────────────────────────── */
let _modalBuilt = false;

function buildModal() {
  if (_modalBuilt) return;
  _modalBuilt = true;

  const overlay = document.getElementById('country-modal-overlay');
  if (!overlay) return;

  /* Apply real currencies BEFORE grouping so data-country-currency is correct */
  if (typeof window.__applyRealCurrencies === 'function') {
    window.__applyRealCurrencies();
  }

  /* Group countries by continent */
  const rawItems = Array.from(overlay.querySelectorAll('#country-modal-list .country-modal__item'));
  const groups   = {};
  rawItems.forEach(btn => {
    const region = getRegion(btn.dataset.countryIso);
    if (!groups[region]) groups[region] = [];
    groups[region].push(btn);
  });

  const listEl = overlay.querySelector('#country-modal-list');
  listEl.innerHTML = '';

  REGION_ORDER.forEach(region => {
    if (!groups[region] || !groups[region].length) return;
    const section = document.createElement('div');
    section.className      = 'country-modal__region';
    section.dataset.region = region;

    const heading = document.createElement('h3');
    heading.className   = 'country-modal__region-title';
    heading.textContent = region;
    section.appendChild(heading);

    const grid = document.createElement('div');
    grid.className = 'country-modal__grid';
    groups[region].forEach(btn => grid.appendChild(btn));
    section.appendChild(grid);
    listEl.appendChild(section);
  });

  /* Search */
  const searchInput = overlay.querySelector('.country-modal__search');
  const noResults   = overlay.querySelector('.country-modal__no-results');

  searchInput.addEventListener('input', () => {
    const q = searchInput.value.trim().toLowerCase();
    let total = 0;
    overlay.querySelectorAll('.country-modal__item').forEach(btn => {
      // Search by country name OR currency code (e.g. "USD", "INR", "EUR")
      const nameMatch = (btn.dataset.countryName || '').toLowerCase().includes(q);
      const currencyMatch = (btn.dataset.countryCurrency || '').toLowerCase().includes(q);
      const match = !q || nameMatch || currencyMatch;
      btn.style.display = match ? '' : 'none';
      if (match) total++;
    });
    overlay.querySelectorAll('.country-modal__region').forEach(sec => {
      const anyVisible = Array.from(sec.querySelectorAll('.country-modal__item')).some(b => b.style.display !== 'none');
      sec.style.display = anyVisible ? '' : 'none';
    });
    noResults.classList.toggle('visible', total === 0);
  });

  /* Country click → sync Bucks then submit */
  overlay.addEventListener('click', e => {
    const btn = e.target.closest('.country-modal__item');
    if (!btn) return;
    submitLocalization(btn.dataset.countryIso, btn.dataset.countryCurrency);
  });

  /* Close button */
  overlay.querySelector('.country-modal__close').addEventListener('click', closeModal);

  /* Backdrop click (clicking outside modal content) */
  overlay.addEventListener('click', e => {
    if (e.target === overlay) closeModal();
  });

  /* Escape key */
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeModal(); });
}

function openModal() {
  buildModal();
  const overlay = document.getElementById('country-modal-overlay');
  if (!overlay) { console.warn('[localization] country-modal-overlay not found'); return; }
  /* Re-apply real currencies every time modal opens — ensures badges are always correct */
  if (typeof window.__applyRealCurrencies === 'function') {
    window.__applyRealCurrencies();
  }
  overlay.classList.add('is-open');
  document.body.classList.add('modal-open');
  const search = overlay.querySelector('.country-modal__search');
  if (search) { search.value = ''; search.dispatchEvent(new Event('input')); search.focus(); }
}

function closeModal() {
  const overlay = document.getElementById('country-modal-overlay');
  if (overlay) overlay.classList.remove('is-open');
  document.body.classList.remove('modal-open');
}

function submitLocalization(isoCode, currencyCode) {
  closeModal();

  /* Determine correct currency based on country */
  /* India always INR, Europe always EUR, everything else USD */
  var CURRENCY_MAP = {
    AD:'EUR',AL:'EUR',AT:'EUR',AX:'EUR',BA:'EUR',BE:'EUR',BG:'EUR',BL:'EUR',
    BY:'EUR',CH:'EUR',CY:'EUR',CZ:'EUR',DE:'EUR',DK:'EUR',EE:'EUR',ES:'EUR',
    FI:'EUR',FR:'EUR',GB:'EUR',GE:'EUR',GF:'EUR',GI:'EUR',GP:'EUR',GR:'EUR',
    HR:'EUR',HU:'EUR',IE:'EUR',IS:'EUR',IT:'EUR',LI:'EUR',LT:'EUR',LU:'EUR',
    LV:'EUR',MC:'EUR',MD:'EUR',ME:'EUR',MF:'EUR',MK:'EUR',MQ:'EUR',MT:'EUR',
    NL:'EUR',NO:'EUR',PL:'EUR',PM:'EUR',PT:'EUR',RE:'EUR',RO:'EUR',RS:'EUR',
    RU:'EUR',SE:'EUR',SI:'EUR',SK:'EUR',SM:'EUR',TR:'EUR',UA:'EUR',VA:'EUR',
    XK:'EUR',YT:'EUR',
    IN:'INR'
  };
  var resolvedCurrency = CURRENCY_MAP[isoCode] || 'USD';

  /* Clear ALL previous currency keys first */
  var exp = new Date(); exp.setFullYear(exp.getFullYear() + 1);
  var expPast = new Date(0);
  ['bucksCurrency','bucks_currency','currency','cart_currency'].forEach(function(k) {
    document.cookie = k + '=; path=/; expires=' + expPast.toUTCString();
  });
  try {
    ['bucksCurrency','bucks_currency','currency','nova_currency','mlveda_currency','user_chosen_currency'].forEach(function(k) {
      localStorage.removeItem(k);
    });
  } catch(e) {}

  /* Now set the correct currency */
  setBucksCurrency(resolvedCurrency);
  try {
    localStorage.setItem('user_country_manual', isoCode + ':' + resolvedCurrency);
    localStorage.setItem('user_chosen_currency', resolvedCurrency);
    localStorage.removeItem('country_auto_set');
  } catch(e) {}

  /* POST to Shopify localization to switch market/catalog */
  var forms = Array.from(document.querySelectorAll('form.localization-form'));
  var form  = forms.find(function(f) { return f.querySelector('input[name="country_code"]'); }) || forms[0];

  if (form) {
    var hiddenInput = form.querySelector('input[name="country_code"]');
    if (hiddenInput) hiddenInput.value = isoCode;

    try {
      var returnPath = window.location.pathname + '?currency=' + encodeURIComponent(resolvedCurrency);
      var actionUrl  = new URL(form.action || '/localization', window.location.origin);
      actionUrl.searchParams.set('return_to', returnPath);
      form.action = actionUrl.toString();
    } catch(e) {}

    form.submit();
  } else {
    window.location.href = window.location.pathname + '?currency=' + encodeURIComponent(resolvedCurrency);
  }
}


function switchBucksCurrency(currencyCode) {
  // Method 1: click the matching option in Bucks hidden select
  const sel = document.getElementById('bucksSelector');
  if (sel) {
    sel.value = currencyCode;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  }
  // Method 2: click the matching li in Bucks dropdown
  document.querySelectorAll('.buckscc-select-options li').forEach(li => {
    if (li.getAttribute('rel') === currencyCode || li.id === currencyCode) {
      li.click();
    }
  });
  // Method 3: Bucks global API if available
  try {
    if (window.BucksCC && window.BucksCC.setCurrency) window.BucksCC.setCurrency(currencyCode);
    if (window.bucks && window.bucks.setCurrency) window.bucks.setCurrency(currencyCode);
  } catch(e) {}
}

/* ─────────────────────────────────────────────
   Hide Bucks floating/embed UI — keep conversion running
   ───────────────────────────────────────────── */
function hideBucksUI() {
  // Hide the floating box
  document.querySelectorAll('.buckscc-floating-box, .buckscc-currency-box').forEach(el => {
    if (!el.closest('#footer-bucks-embed')) {
      el.style.cssText = 'display:none!important';
    }
  });
  // Keep #footer-bucks-embed itself invisible (just an anchor)
  const embed = document.getElementById('footer-bucks-embed');
  if (embed) {
    embed.style.cssText = 'position:absolute!important;width:1px!important;height:1px!important;overflow:hidden!important;opacity:0!important;pointer-events:none!important;clip:rect(0,0,0,0)!important';
  }
}

/* ─────────────────────────────────────────────
   Fix EUR comma decimal → dot decimal
   Bucks uses European formatting for EUR (e.g. €176,21)
   We force English dot format (€176.21) for all currencies
   ───────────────────────────────────────────── */
function fixPriceFormat() {
  /* Target all elements Bucks converts — .buckscc-money and .money spans */
  document.querySelectorAll('.buckscc-money, .money').forEach(function(el) {
    var text = el.textContent || '';
    /* Detect European format: currency symbol + digits + comma + 2 digits at end
       e.g. €176,21 or €1.234,56 */
    if (/[€£¥₹]/.test(text) || text.match(/^\s*[A-Z]{0,3}\s*[\d.,]+\s*[A-Z]{0,3}\s*$/)) {
      /* European format: has comma before exactly 2 digits at end */
      if (text.match(/,\d{2}(\s*[A-Z]*\s*)$/)) {
        /* Remove thousand-separator dots first, then convert decimal comma to dot */
        var fixed = text
          .replace(/(\d)\.(\d{3})/g, '$1$2')  /* remove 1.234 thousand sep */
          .replace(/,(\d{2})(\s*[A-Z]*\s*)$/, '.$1$2'); /* comma decimal → dot */
        if (fixed !== text) el.textContent = fixed;
      }
    }
  });
}

/* Observe DOM for Bucks price updates and fix format */
function observePriceChanges() {
  fixPriceFormat();
  var observer = new MutationObserver(function() {
    fixPriceFormat();
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}

/* ─────────────────────────────────────────────
   Boot
   ───────────────────────────────────────────── */
function boot() {
  hideBucksUI();
  setTimeout(hideBucksUI, 1000);
  setTimeout(hideBucksUI, 3000);

  /* Wire the "India ↓" trigger button to open the modal */
  document.querySelectorAll('.country-modal-trigger').forEach(function(btn) {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      openModal();
    });
  });

  /* Fix price format after Bucks renders */
  [500, 1000, 2000, 3000].forEach(function(ms) {
    setTimeout(fixPriceFormat, ms);
  });
  window.addEventListener('load', observePriceChanges);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

/* Wire trigger button — runs immediately AND after DOM ready */
function wireTrigger() {
  document.querySelectorAll('.country-modal-trigger').forEach(function(btn) {
    btn.onclick = function(e) {
      e.preventDefault();
      openModal();
    };
  });
}
wireTrigger();
document.addEventListener('DOMContentLoaded', wireTrigger);
window.addEventListener('load', wireTrigger);